import React, { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Avatar,
  Switch,
  Tooltip,
} from '@mui/material';
import { CommercialDateModeSection } from '../../shared/CommercialDateTimePicker';
import type { CommercialDateMode } from '../../../../utils/sales/commercial-sale-date';
import {
  CheckCircle as CheckIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  SalesOrder,
  ExchangeFormData,
  ExchangeSummary,
  SelectedProduct,
} from '../../../../types/sales.types';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import {
  formatExchangeDiscountDisplay,
  formatExchangeDiscountLineLabel,
  getEffectiveLineDiscountAmount,
  hasProductDiscount,
} from './exchangeDiscount.utils';
import {
  getExchangeCreditViolationMessage,
  isExchangeSummaryInvalid,
} from './exchangePricing.utils';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import RemoteOriginChip from '../../../sales/shared/RemoteOriginChip';
import * as S from './ExchangeDrawer.styles';

interface ExchangeStep3ConfirmationProps {
  order: SalesOrder;
  exchangeData: ExchangeFormData;
  exchangeSummary: ExchangeSummary;
  formatCurrency: (amount: number) => string;
  getProductFinalPrice: (product: SelectedProduct) => number;
  onToggleDelivered: (productId: number, isDelivered: boolean) => void;
  canModifySaleDate: boolean;
  exchangeDateMode: CommercialDateMode | null;
  exchangeDate: Date;
  exchangeDateModeError: boolean;
  onExchangeDateModeChange: (mode: CommercialDateMode) => void;
  onExchangeDateChange: (date: Date) => void;
}

const SummaryRow: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
  labelVariant?: 'body2' | 'body1';
  valueWeight?: number;
  indent?: boolean;
}> = ({ label, value, valueColor, labelVariant = 'body2', valueWeight = 600, indent = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      mb: 1,
      pl: indent ? 2 : 0,
    }}
  >
    <Typography variant={labelVariant} color={indent ? 'text.secondary' : 'text.secondary'}>
      {label}
    </Typography>
    <Typography variant={labelVariant} fontWeight={valueWeight} color={valueColor}>
      {value}
    </Typography>
  </Box>
);

const getPaymentMethodLabel = (methodCode: string): string => {
  if (methodCode === PAYMENT_METHODS.CASH) return 'Efectivo';
  if (methodCode === PAYMENT_METHODS.CARD) return 'Tarjeta';
  if (methodCode === PAYMENT_METHODS.QR) return 'QR';
  return methodCode;
};

const ExchangeStep3Confirmation: React.FC<ExchangeStep3ConfirmationProps> = ({
  order,
  exchangeData,
  exchangeSummary,
  formatCurrency,
  getProductFinalPrice,
  onToggleDelivered,
  canModifySaleDate,
  exchangeDateMode,
  exchangeDate,
  exchangeDateModeError,
  onExchangeDateModeChange,
  onExchangeDateChange,
}) => {
  const sellingLocationId = order.location?.id;
  const showDiscountColumns = useMemo(() => {
    if (!exchangeData.discountConfig?.enabled) return false;
    return exchangeData.newProducts.some(hasProductDiscount);
  }, [exchangeData.discountConfig?.enabled, exchangeData.newProducts]);

  const productsWithDiscount = useMemo(
    () => exchangeData.newProducts.filter(hasProductDiscount),
    [exchangeData.newProducts],
  );

  const exchangedItems = useMemo(
    () => order.items.filter((item) => exchangeData.itemsToExchange.includes(item.id)),
    [order.items, exchangeData.itemsToExchange],
  );

  const exchangeSummaryInvalid = isExchangeSummaryInvalid(
    exchangeSummary.newItemsTotal,
    exchangeSummary.paidForExchangedItems,
    exchangeSummary.isSummaryCoherent,
  );

  const dashboardPaymentCaption = useMemo(() => {
    if (exchangeSummary.difference <= 0) {
      return exchangeSummary.requiresRefund ? 'Sin cargo adicional' : 'Sin cargo adicional';
    }
    if (exchangeSummary.totalPayments > 0) {
      if (exchangeSummary.remainingBalance > 0) {
        return `Cobrado: ${formatCurrency(exchangeSummary.totalPayments)} · Pendiente: ${formatCurrency(exchangeSummary.remainingBalance)}`;
      }
      if (exchangeSummary.change > 0) {
        return `Cobrado: ${formatCurrency(exchangeSummary.totalPayments)} · Cambio: ${formatCurrency(exchangeSummary.change)}`;
      }
      return `Cobrado: ${formatCurrency(exchangeSummary.totalPayments)}`;
    }
    return 'Por pagar en este intercambio';
  }, [exchangeSummary, formatCurrency]);

  return (
    <S.ConfirmationLayout>
      <S.ConfirmationLeftColumn>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            backgroundColor: alpha(
              theme.palette.success.main,
              theme.palette.mode === 'dark' ? 0.14 : 0.06,
            ),
            borderRadius: '8px',
            padding: '10px 14px',
            mb: 3,
          })}
        >
          <InfoIcon
            sx={(theme) => ({
              fontSize: 18,
              color: theme.palette.success.main,
              mt: '2px',
              flexShrink: 0,
            })}
          />
          <Box>
            <Typography
              variant="body2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
                mb: 0.5,
              })}
            >
              Resumen del Intercambio
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
                lineHeight: 1.5,
              })}
            >
              Revise los detalles antes de confirmar el intercambio.
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <S.DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(239,68,68,0.16)',
                      color: 'error.main',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <ShoppingCartIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4 }} color="error">
                      Piezas intercambiadas
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'error.dark', lineHeight: 1.2 }}
                    >
                      {formatCurrency(exchangeSummary.originalItemsTotal)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                    >
                      Crédito del intercambio:{' '}
                      {formatCurrency(exchangeSummary.paidForExchangedItems)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {exchangeData.itemsToExchange.length} pieza(s)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </S.DashboardCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <S.DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(16,185,129,0.16)',
                      color: 'success.main',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4 }} color="success.main">
                      Piezas nuevas
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'success.dark', lineHeight: 1.2 }}
                    >
                      {formatCurrency(exchangeSummary.newItemsTotal)}
                    </Typography>
                    {exchangeSummary.discountAmount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                      >
                        Descuentos aplicados: −{formatCurrency(exchangeSummary.discountAmount)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {exchangeData.newProducts.length} pieza(s)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </S.DashboardCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <S.DashboardCard variant="outlined">
              <CardContent sx={{ py: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      bgcolor: 'rgba(59,130,246,0.16)',
                      color: '#2563eb',
                      width: 36,
                      height: 36,
                    }}
                  >
                    <MoneyIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4, color: '#2563eb' }}>
                      A pagar por intercambio
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: '#1d4ed8', lineHeight: 1.2 }}
                    >
                      {formatCurrency(Math.max(0, exchangeSummary.difference))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dashboardPaymentCaption}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </S.DashboardCard>
          </Grid>
        </Grid>

        {exchangeSummaryInvalid && (
          <Box
            sx={(theme) => ({
              mb: 2.5,
              p: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.error.main}`,
              bgcolor: alpha(theme.palette.error.main, 0.06),
            })}
          >
            <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
              {!exchangeSummary.isSummaryCoherent
                ? 'El resumen del intercambio no cuadra. Vuelve al paso anterior y ajusta los descuentos.'
                : getExchangeCreditViolationMessage(
                    exchangeSummary.paidForExchangedItems,
                    formatCurrency,
                  )}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <S.SectionBox>
              <S.SectionHeader>
                <ShoppingCartIcon color="error" />
                <Typography
                  variant="subtitle1"
                  sx={(theme) => ({
                    fontWeight: 700,
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.info.light
                        : theme.palette.info.dark,
                  })}
                >
                  Piezas a intercambiar
                </Typography>
              </S.SectionHeader>
              <Box sx={{ p: 2 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={(theme) => ({
                    border: 0,
                    borderRadius: 0,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: 'none',
                  })}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={(theme) => ({
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.06)
                              : theme.palette.grey[50],
                          '& th': { fontWeight: 700, color: 'text.secondary' },
                        })}
                      >
                        <TableCell>Producto</TableCell>
                        <TableCell align="right">Precio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exchangedItems.map((item) => (
                        <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600">
                              {item.warehouseInventoryProduct.product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.warehouseInventoryProduct.productCodeUnique}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="700" color="error.main">
                              {formatCurrency(item.finalPrice)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </S.SectionBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <S.SectionBox>
              <S.SectionHeader>
                <CheckIcon color="success" />
                <Typography
                  variant="subtitle1"
                  sx={(theme) => ({
                    fontWeight: 700,
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.info.light
                        : theme.palette.info.dark,
                  })}
                >
                  Piezas nuevas
                </Typography>
              </S.SectionHeader>
              <Box sx={{ p: 2 }}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={(theme) => ({
                    border: 0,
                    borderRadius: 0,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: 'none',
                  })}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={(theme) => ({
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? alpha(theme.palette.common.white, 0.06)
                              : theme.palette.grey[50],
                          '& th': { fontWeight: 700, color: 'text.secondary' },
                        })}
                      >
                        <TableCell>Producto</TableCell>
                        {showDiscountColumns ? (
                          <>
                            <TableCell align="right">Lista</TableCell>
                            <TableCell align="right">Descuento</TableCell>
                            <TableCell align="right">Final</TableCell>
                          </>
                        ) : (
                          <TableCell align="right">Precio</TableCell>
                        )}
                        <TableCell align="center">Entregada</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exchangeData.newProducts.map((product) => {
                        const productId = product.warehouseInventoryProduct.id;
                        const isRemote = isRemoteCartItem(product, sellingLocationId);
                        return (
                          <TableRow key={productId} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="600">
                                {product.warehouseInventoryProduct.product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {product.warehouseInventoryProduct.productCodeUnique}
                              </Typography>
                              {isRemote && (
                                <Box sx={{ mt: 0.5 }}>
                                  <RemoteOriginChip
                                    locationName={product.warehouseInventoryProduct.location?.name}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.25 }}
                                  >
                                    Pendiente de traslado
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            {showDiscountColumns ? (
                              <>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {formatCurrency(product.originalPrice)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {hasProductDiscount(product) ? (
                                    <Typography variant="body2" color="error.main" fontWeight={600}>
                                      −
                                      {formatExchangeDiscountDisplay(
                                        formatCurrency,
                                        product,
                                        getProductFinalPrice(product),
                                      )}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="700" color="success.main">
                                    {formatCurrency(getProductFinalPrice(product))}
                                  </Typography>
                                </TableCell>
                              </>
                            ) : (
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="700" color="success.main">
                                  {formatCurrency(getProductFinalPrice(product))}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  isRemote
                                    ? 'Las piezas de otras sucursales o almacén se entregan cuando lleguen'
                                    : 'Marcar si el cliente se lleva esta pieza ahora'
                                }
                              >
                                <span>
                                  <Switch
                                    size="small"
                                    checked={Boolean(product.isDelivered)}
                                    disabled={isRemote}
                                    onChange={(_, checked) => onToggleDelivered(productId, checked)}
                                    inputProps={{ 'aria-label': `Entregar pieza ${productId}` }}
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </S.SectionBox>
          </Grid>
        </Grid>

        {exchangeData.exchangeNotes ? (
          <Card variant="outlined" sx={{ bgcolor: 'background.paper', mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Notas del intercambio
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {exchangeData.exchangeNotes}
              </Typography>
            </CardContent>
          </Card>
        ) : null}
      </S.ConfirmationLeftColumn>

      <S.ConfirmationRightColumn>
        <S.SectionBox>
          <S.SectionHeader>
            <ReceiptIcon color="primary" />
            <Typography
              variant="subtitle1"
              sx={(theme) => ({
                fontWeight: 700,
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.info.light
                    : theme.palette.info.dark,
              })}
            >
              Resumen financiero
            </Typography>
          </S.SectionHeader>
          <Box sx={{ p: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1.5, color: 'success.main' }}
            >
              Piezas nuevas
            </Typography>
            <SummaryRow
              label={`Subtotal (${exchangeData.newProducts.length} pieza${exchangeData.newProducts.length === 1 ? '' : 's'})`}
              value={formatCurrency(exchangeSummary.newItemsSubtotal)}
            />

            {exchangeSummary.markupAmount > 0 && (
              <SummaryRow
                label="Incrementos"
                value={`+${formatCurrency(exchangeSummary.markupAmount)}`}
                valueColor="success.main"
              />
            )}

            {productsWithDiscount.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.75 }}
                >
                  Descuentos por pieza:
                </Typography>
                {productsWithDiscount.map((product) => {
                  const finalPrice = getProductFinalPrice(product);
                  return (
                    <SummaryRow
                      key={product.warehouseInventoryProduct.id}
                      indent
                      label={`${product.warehouseInventoryProduct.product.name} — ${formatExchangeDiscountLineLabel(product, finalPrice)}`}
                      value={`−${formatCurrency(getEffectiveLineDiscountAmount(product.adjustedPrice, finalPrice))}`}
                      valueColor="error.main"
                    />
                  );
                })}
                <SummaryRow
                  indent
                  label="Total descuentos"
                  value={`−${formatCurrency(exchangeSummary.discountAmount)}`}
                  valueColor="error.main"
                  valueWeight={700}
                />
              </Box>
            )}

            <SummaryRow
              label="Total piezas nuevas"
              value={formatCurrency(exchangeSummary.newItemsTotal)}
              valueColor="success.main"
              labelVariant="body1"
              valueWeight={700}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'error.main' }}>
              Intercambio
            </Typography>
            <SummaryRow
              label="Crédito del intercambio"
              value={formatCurrency(exchangeSummary.paidForExchangedItems)}
            />

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}
            >
              A pagar hoy
            </Typography>
            <SummaryRow
              label="Diferencia a pagar"
              value={formatCurrency(exchangeSummary.difference)}
              labelVariant="body1"
              valueWeight={700}
              valueColor="primary.main"
            />

            {exchangeData.payments?.map((payment) => (
              <SummaryRow
                key={payment.id}
                label={getPaymentMethodLabel(payment.method)}
                value={formatCurrency(payment.amount)}
                valueColor="success.main"
                valueWeight={700}
              />
            ))}

            {(exchangeSummary.remainingBalance > 0 || exchangeSummary.change > 0) && (
              <Box sx={{ mt: 1 }}>
                {exchangeSummary.remainingBalance > 0 && (
                  <SummaryRow
                    label="Saldo pendiente"
                    value={formatCurrency(exchangeSummary.remainingBalance)}
                    labelVariant="body1"
                    valueWeight={700}
                    valueColor="warning.main"
                  />
                )}
                {exchangeSummary.change > 0 && (
                  <SummaryRow
                    label="Cambio"
                    value={formatCurrency(exchangeSummary.change)}
                    labelVariant="body1"
                    valueWeight={700}
                    valueColor="success.main"
                  />
                )}
              </Box>
            )}

            {exchangeSummary.difference <= 0 &&
              exchangeSummary.totalPayments === 0 &&
              exchangeSummary.remainingBalance === 0 && (
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    backgroundColor: alpha(
                      theme.palette.success.main,
                      theme.palette.mode === 'dark' ? 0.14 : 0.06,
                    ),
                    borderRadius: '8px',
                    padding: '10px 14px',
                    mt: 2,
                  })}
                >
                  <InfoIcon
                    sx={(theme) => ({
                      fontSize: 18,
                      color: theme.palette.success.main,
                      mt: '2px',
                      flexShrink: 0,
                    })}
                  />
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === 'dark'
                          ? theme.palette.success.light
                          : theme.palette.success.dark,
                      lineHeight: 1.5,
                    })}
                  >
                    No se requiere pago adicional. El total de las piezas nuevas cubre el crédito
                    del intercambio.
                  </Typography>
                </Box>
              )}
          </Box>
        </S.SectionBox>

        {canModifySaleDate && (
          <Box sx={{ mt: 2 }}>
            <CommercialDateModeSection
              title="Fecha del intercambio"
              mode={exchangeDateMode}
              onModeChange={onExchangeDateModeChange}
              value={exchangeDate}
              onChange={onExchangeDateChange}
              pickerLabel="Fecha y hora del intercambio"
              currentHelperText="Se usará la fecha y hora en que se confirme el intercambio."
              groupAriaLabel="Fecha comercial del intercambio"
              error={exchangeDateModeError}
            />
          </Box>
        )}
      </S.ConfirmationRightColumn>
    </S.ConfirmationLayout>
  );
};

export default ExchangeStep3Confirmation;
